import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantDocument.css";
import { API_BASE } from "../../../ApiUrl";
import DocumentTabs from "./DocumentTabs";

const API_URL = `${API_BASE}/tenant-documents/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const DOCUMENT_TYPE_OPTIONS = [
  { value: "REGISTRATION", label: "Registration" },
  { value: "TAX", label: "Tax" },
  { value: "ADDRESS", label: "Address Proof" },
  { value: "AUTHORISATION", label: "Authorisation" },
  { value: "LICENCE", label: "Licence" },
];

const emptyDocument = {
  id: null,
  document_type: "",
  file: null,
  file_name: "",
  existing_file_url: "",
  issue_date: "",
  expiry_date: "",
};

const toDateInput = (v) => (v ? String(v).substring(0, 10) : "");

const AddTenantDocument = () => {
  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([{ ...emptyDocument }]);
  const [tenantId, setTenantId] = useState(null);
  const [userId, setUserId] = useState(null);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // ---- Get tenant + user ids ----
  useEffect(() => {
    const tenantUser = getTenantUser();
    if (tenantUser?.tenant_id) setTenantId(Number(tenantUser.tenant_id));
    if (tenantUser?.user_id) setUserId(Number(tenantUser.user_id));
  }, []);

  // ---- Fetch documents ----
  useEffect(() => {
    if (tenantId) fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["tenant_documents"],
        },
        { headers: { "Content-Type": "application/json" } },
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.tenant_documents) {
        rows = result.data.tenant_documents;
      } else if (result?.tenant_documents) {
        rows = result.tenant_documents;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setDocuments(rows);
    } catch (error) {
      console.error("Error fetching tenant documents:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load documents",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading documents.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Form handlers ----
  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [name]: value } : d)),
    );
  };

  const handleFileChange = (index, e) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) =>
      prev.map((d, i) =>
        i === index
          ? { ...d, file, file_name: file ? file.name : d.file_name }
          : d,
      ),
    );
  };

  const handleAdd = () => {
    setFormData([{ ...emptyDocument }]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, { ...emptyDocument }]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (d) => {
    setFormData([
      {
        id: d.id,
        document_type: d.document_type || "",
        file: null,
        file_name: d.file ? String(d.file).split("/").pop() : "",
        existing_file_url: d.file || "",
        issue_date: toDateInput(d.issue_date),
        expiry_date: toDateInput(d.expiry_date),
      },
    ]);
    setEditingId(d.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (d) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Document?",
      text: `Are you sure you want to delete this ${
        d.document_type || "document"
      }?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}${d.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Document deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchDocuments();
    } catch (error) {
      console.error("Error deleting document:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text: error.response?.data?.detail || "Unable to delete the document.",
      });
    }
  };

  // ---- Validation ----
  const validateRow = (d, index) => {
    const label = `Document ${index + 1}`;

    const isEmptyRow = !d.document_type && !d.file && !d.file_name && !d.id;
    if (isEmptyRow) return true;

    if (!d.document_type) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please select a Document Type for ${label}.`,
      });
      return false;
    }

    if (!isEditing && !d.file && !d.existing_file_url) {
      Swal.fire({
        icon: "warning",
        title: "File Required",
        text: `Please upload a file for ${label}.`,
      });
      return false;
    }

    if (d.file && d.file.size > 10 * 1024 * 1024) {
      Swal.fire({
        icon: "warning",
        title: "File Too Large",
        text: `File for ${label} must be 10 MB or smaller.`,
      });
      return false;
    }

    if (d.issue_date && d.expiry_date && d.expiry_date < d.issue_date) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Dates",
        text: `Expiry Date for ${label} cannot be before Issue Date.`,
      });
      return false;
    }

    return true;
  };

  const buildFormData = (d) => {
    const fd = new FormData();
    fd.append("document_type", d.document_type);
    fd.append("tenant", tenantId);

    if (d.issue_date) fd.append("issue_date", d.issue_date);
    if (d.expiry_date) fd.append("expiry_date", d.expiry_date);
    if (d.file) fd.append("file", d.file);

    return fd;
  };

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`,
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant.",
      });
      return;
    }

    for (let i = 0; i < formData.length; i++) {
      if (!validateRow(formData[i], i)) return;
    }

    const activeRows = formData.filter(
      (d) => d.document_type || d.file || d.file_name || d.id,
    );

    const MULTIPART_CONFIG = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    try {
      setLoading(true);

      if (isEditing) {
        const fd = buildFormData(formData[0]);
        await axios.put(`${API_URL}${editingId}/`, fd, MULTIPART_CONFIG);

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        for (const d of activeRows) {
          const fd = buildFormData(d);
          await axios.post(API_URL, fd, MULTIPART_CONFIG);
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} document${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false,
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyDocument }]);

      await fetchDocuments();
    } catch (error) {
      console.error("Error saving document:", error);

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyDocument }]);
  };

  // ---- Lookup helpers ----
  const getDocumentTypeLabel = (type) => {
    const found = DOCUMENT_TYPE_OPTIONS.find((o) => o.value === type);
    return found?.label || type || "-";
  };

  const getFileName = (d) => {
    if (!d.file) return "-";
    return String(d.file).split("/").pop();
  };

  const handleViewDocument = (d) => {
    if (!d.file) {
      Swal.fire({
        icon: "info",
        title: "No file",
        text: "No file is attached to this document.",
      });
      return;
    }

    const url = d.file.startsWith("http")
      ? d.file
      : `${API_BASE.replace(/\/api\/?$/, "")}${d.file}`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-doc-page">
          <DocumentTabs />

          {!showForm ? (
            <>
              {/* ==================================================
                  LIST HEADER
              ================================================== */}

              <div className="tenant-doc-page-header">
                <div>
                  <h2>Tenant Documents</h2>
                </div>

                <button
                  type="button"
                  className="tenant-doc-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add Document
                </button>
              </div>

              {/* ==================================================
                  TABLE
              ================================================== */}

              <div className="tenant-doc-list-card">
                {loading ? (
                  <div className="tenant-doc-loading">Loading documents...</div>
                ) : documents.length === 0 ? (
                  <div className="tenant-doc-empty">
                    <div className="tenant-doc-empty-icon">+</div>
                    <h3>No Documents Found</h3>
                    <p>
                      There are no documents available for the current tenant.
                    </p>
                    <button
                      type="button"
                      className="tenant-doc-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Document
                    </button>
                  </div>
                ) : (
                  <div className="tenant-doc-table-wrapper">
                    <table className="tenant-doc-table">
                      <thead>
                        <tr>
                          <th>Document Type</th>
                          <th>File</th>
                          <th>Issue Date</th>
                          <th>Expiry Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {documents.map((d) => (
                          <tr key={d.id}>
                            <td>
                              <span className="tenant-doc-type-pill">
                                {getDocumentTypeLabel(d.document_type)}
                              </span>
                            </td>

                            <td>
                              {d.file ? (
                                <button
                                  type="button"
                                  className="tenant-doc-view-btn"
                                  onClick={() => handleViewDocument(d)}
                                >
                                  View
                                </button>
                              ) : (
                                <span className="tenant-doc-muted">-</span>
                              )}
                            </td>

                            <td className="tenant-doc-muted">
                              {d.issue_date || "-"}
                            </td>

                            <td className="tenant-doc-muted">
                              {d.expiry_date || "-"}
                            </td>

                            <td>
                              <div className="tenant-doc-actions">
                                <button
                                  type="button"
                                  className="tenant-doc-edit-btn"
                                  onClick={() => handleEdit(d)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="tenant-doc-delete-btn"
                                  onClick={() => handleDelete(d)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ==================================================
                  ADD / EDIT HEADER
              ================================================== */}

              <div className="tenant-doc-page-header">
                <div>
                  <h2>{isEditing ? "Edit Document" : "Create Documents"}</h2>
                </div>

                <button
                  type="button"
                  className="tenant-doc-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {/* ==================================================
                  FORM
              ================================================== */}

              <form onSubmit={handleSubmit}>
                {formData.map((d, index) => (
                  <div
                    className="tenant-doc-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="tenant-doc-form-body">
                      <div className="tenant-doc-record-header">
                        <h4>Document {index + 1}</h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="tenant-doc-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="tenant-doc-form-grid">
                        <div className="tenant-doc-form-group">
                          <label>
                            Document Type <span>*</span>
                          </label>
                          <select
                            name="document_type"
                            value={d.document_type}
                            onChange={(e) => handleChange(index, e)}
                            required
                          >
                            <option value="">Select document type</option>
                            {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-doc-form-group">
                          <label>Issue Date</label>
                          <input
                            type="date"
                            name="issue_date"
                            value={d.issue_date}
                            onChange={(e) => handleChange(index, e)}
                          />
                        </div>

                        <div className="tenant-doc-form-group">
                          <label>Expiry Date</label>
                          <input
                            type="date"
                            name="expiry_date"
                            value={d.expiry_date}
                            onChange={(e) => handleChange(index, e)}
                          />
                        </div>

                        <div className="tenant-doc-form-group tenant-doc-form-group--full">
                          <label>File {!isEditing && <span>*</span>}</label>
                          <div className="tenant-doc-file-row">
                            <input
                              id={`tenant-doc-file-${index}`}
                              type="file"
                              onChange={(e) => handleFileChange(index, e)}
                            />

                            {d.file_name && (
                              <span className="tenant-doc-file-name">
                                {d.file_name}
                              </span>
                            )}
                          </div>
                          <p className="tenant-doc-hint">
                            {isEditing
                              ? "Leave empty to keep the existing file."
                              : "Upload the proof document (PDF, image, DOCX, etc.)."}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="tenant-doc-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another Document
                  </button>
                )}

                <div className="tenant-doc-form-actions">
                  <button
                    type="button"
                    className="tenant-doc-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="tenant-doc-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                        ? "Update Document"
                        : "Save Documents"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTenantDocument;
